import { AlertTriangle, Upload } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";
import { Alert, Button, Input, Modal, Textarea } from "@/components/common";
import {
  useDeleteDocument,
  useUpdateDocument,
  useUploadDocument,
} from "@/hooks/useToolkit";
import type { ToolkitDocument } from "@/types/toolkit";
import { formatFileSize } from "./format";

/** Suggested categories; any other name typed in becomes a new category. */
export const DEFAULT_CATEGORIES = [
  "RODO",
  "APK",
  "Wypowiedzenia",
  "Pełnomocnictwa",
  "Szkody",
  "Inne",
];

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.odt,.ods";
const MAX_BYTES = 10 * 1024 * 1024;

function errorMessage(error: unknown, fallback: string) {
  return (error as { message?: string } | null)?.message ?? fallback;
}

/** `document` null uploads a new one; otherwise edits it (the file is optional). */
export interface DocumentTarget {
  document: ToolkitDocument | null;
}

export function DocumentModal({
  target,
  categories,
  onClose,
}: {
  target: DocumentTarget | null;
  categories: string[];
  onClose: () => void;
}) {
  const upload = useUploadDocument();
  const update = useUpdateDocument();
  const mutation = target?.document ? update : upload;
  const listId = useId();
  const fileInputId = useId();

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const [openedFor, setOpenedFor] = useState<DocumentTarget | null>(null);
  if (target !== openedFor) {
    setOpenedFor(target);
    const d = target?.document;
    setTitle(d?.title ?? "");
    setCategory(d?.category ?? "");
    setDescription(d?.description ?? "");
    setFile(null);
  }

  const isNew = target !== null && target.document === null;
  const fileError =
    file && file.size > MAX_BYTES
      ? "Plik jest za duży — maksymalnie 10 MB."
      : undefined;
  const valid =
    title.trim().length > 0 &&
    category.trim().length > 0 &&
    (!isNew || file !== null) &&
    !fileError;

  function handleClose() {
    upload.reset();
    update.reset();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!target || !valid) {
      return;
    }
    const details = {
      title: title.trim(),
      category: category.trim(),
      description: description.trim(),
    };
    const onSuccess = (saved: ToolkitDocument) => {
      toast.success(
        isNew
          ? `Dodano dokument: ${saved.title}.`
          : file
            ? `Wgrano nową wersję: ${saved.title} (v${saved.version}).`
            : `Zapisano zmiany: ${saved.title}.`,
      );
      handleClose();
    };
    if (target.document) {
      update.mutate({ id: target.document.id, details, file }, { onSuccess });
    } else if (file) {
      upload.mutate({ details, file }, { onSuccess });
    }
  }

  const allCategories = [...new Set([...DEFAULT_CATEGORIES, ...categories])];

  return (
    <Modal
      isOpen={target !== null}
      onClose={handleClose}
      title={isNew ? "Nowy dokument" : "Edytuj dokument"}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {mutation.error && (
          <Alert variant="error">
            {errorMessage(mutation.error, "Nie udało się zapisać dokumentu")}
          </Alert>
        )}

        <Input
          label="Nazwa"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="np. Wzór wypowiedzenia OC"
          required
          maxLength={150}
          autoComplete="off"
        />
        <Input
          label="Kategoria"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
          }}
          list={listId}
          placeholder="Wybierz lub wpisz nową"
          required
          maxLength={60}
          autoComplete="off"
        />
        <datalist id={listId}>
          {allCategories.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <Textarea
          label="Opis (opcjonalnie)"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          placeholder="np. kiedy użyć, co wypełnić przed wysłaniem"
          rows={2}
          maxLength={500}
        />

        <div>
          <label
            htmlFor={fileInputId}
            className="mb-1.5 block text-sm font-medium text-sub"
          >
            {isNew ? "Plik" : "Nowa wersja pliku (opcjonalnie)"}
          </label>
          <label
            htmlFor={fileInputId}
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-edge p-3 text-sm text-dim hover:bg-muted"
          >
            <Upload size={18} aria-hidden className="shrink-0" />
            <span className="min-w-0 truncate">
              {file
                ? `${file.name} (${formatFileSize(file.size)})`
                : target?.document
                  ? `Obecnie: ${target.document.fileName} — kliknij, aby podmienić`
                  : "Wybierz plik PDF, Word, Excel lub OpenDocument (do 10 MB)"}
            </span>
          </label>
          <input
            id={fileInputId}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
            }}
          />
          {fileError && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {fileError}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            isLoading={mutation.isPending}
            disabled={!valid}
          >
            {isNew ? "Dodaj dokument" : "Zapisz"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function DeleteDocumentModal({
  document,
  onClose,
}: {
  document: ToolkitDocument | null;
  onClose: () => void;
}) {
  const remove = useDeleteDocument();

  function handleClose() {
    remove.reset();
    onClose();
  }

  return (
    <Modal
      isOpen={document !== null}
      onClose={handleClose}
      title="Usuń dokument"
    >
      <div className="space-y-4">
        {remove.isError && (
          <Alert variant="error">
            {errorMessage(remove.error, "Nie udało się usunąć dokumentu")}
          </Alert>
        )}
        <div className="flex gap-3 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
          <AlertTriangle
            size={20}
            className="shrink-0 text-red-600 dark:text-red-400"
            aria-hidden="true"
          />
          <p className="text-sm text-red-800 dark:text-red-300">
            Dokument <span className="font-semibold">{document?.title}</span>{" "}
            zniknie z Podręcznika Agenta — agenci nie będą mogli go pobrać.
          </p>
        </div>
        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            isLoading={remove.isPending}
            onClick={() => {
              if (document) {
                remove.mutate(document.id, {
                  onSuccess: () => {
                    toast.success("Usunięto dokument.");
                    handleClose();
                  },
                });
              }
            }}
          >
            Usuń dokument
          </Button>
        </div>
      </div>
    </Modal>
  );
}
